import { useContext, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { ReactComponent as CloseIcon } from 'assets/close-icon.svg';
import { CONFIRMATION_HANDLE_TYPES, SMS_ERROR_CODE_DISABLE, TWO_FACTOR_AUTH_TYPES } from 'components/common/constants';
// eslint-disable-next-line max-len
import SwitchConfirmationMethodScreen from 'components/common/ActionConfirmation/components/SwitchConfirmationMethodScreen';
import { ConfirmCodeWrapper } from 'components/common/ConfirmCodeWrapper/ConfirmCodeWrapper';
import i18nContext from 'components/i18n-context';
import { isFullSecurityCode } from 'components/utils';
import { getAvailableConfirmationTypes, getConfirmationActionType, getSecureUserPhone } from 'services/authUtils';
import { getErrorMessageForAlert } from 'services/utils';
import Button from 'uikit/Button/Button';
import './PopUpScheme.scss';

const PopUpInternalActionConfirmationScheme = ({
  isLoading,
  isShowSwitchConfirmationMethodScreen,
  title,
  confirmButtonLabel,
  onClose,
  onConfirm,
  error,
  clearError,
  resendSecurityCode,
  setIsShowSwitchConfirmationMethodScreen,
  handleForceSwitchToAnotherConfirmationType,
  confirmationId,
  module
}) => {
  const i18n = useContext(i18nContext);
  const [alertState, setAlertState] = useState({ type: '', message: '' });
  const [showCountDown, setShowCountDown] = useState(false);
  const [securityCode, setSecurityCode] = useState('');
  const [isDisabled, setIsDisabled] = useState(false);
  const [isConfirmDisabled, setIsConfirmDisabled] = useState(true);
  const currentConfirmationType = getConfirmationActionType();
  const pushPollingIntervalRef = useRef(null);

  useEffect(() => {
    if (currentConfirmationType !== TWO_FACTOR_AUTH_TYPES.GOOGLE_AUTH) {
      setShowCountDown(true);
    }

    // Start polling for push notification confirmation
    if (currentConfirmationType === TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION && confirmationId && module) {
      // For push notifications, we need to continuously call onConfirm with null code
      // The parent component will handle the polling logic and error responses
      pushPollingIntervalRef.current = setInterval(() => {
        onConfirm(null);
      }, 2000); // Poll every 2 seconds
    }

    return () => {
      clearError();
      if (pushPollingIntervalRef.current) {
        clearInterval(pushPollingIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConfirmationType, confirmationId, module]);

  const handleResendCode = async () => {
    clearError();
    setShowCountDown(true);
    setAlertState({ type: 'success', message: i18n.getMessage('pin.alert.sent') });
    isDisabled && setIsDisabled(false);
    await resendSecurityCode();
  };

  const onTimerEnd = () => {
    setShowCountDown(false);
  };

  const onComplete = (code) => {
    setSecurityCode(code);
    if (!isFullSecurityCode(code)) {
      setIsConfirmDisabled(true);
    } else {
      setIsConfirmDisabled(false);
      setAlertState({ type: '', message: '' });
    }
  };

  const handleConfirm = () => {
    // For push notifications, pass null as code
    if (currentConfirmationType === TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION) {
      onConfirm(null);
    } else {
      onConfirm(securityCode);
    }
  };

  useEffect(() => {
    if (SMS_ERROR_CODE_DISABLE?.includes(error?.code)) {
      setIsDisabled(true);
    }
    // Don't show alert for CONFIRMATION_PENDING_ACTION - it's normal during push notification polling
    if (error && error?.code !== 'CONFIRMATION_PENDING_ACTION') {
      setAlertState({ type: 'warning', message: getErrorMessageForAlert(i18n, error) });
      setIsConfirmDisabled(true);
    } else if (!error) {
      if (
        currentConfirmationType === TWO_FACTOR_AUTH_TYPES.SMS ||
        currentConfirmationType === TWO_FACTOR_AUTH_TYPES.WHATSAPP
      ) {
        isDisabled && setIsDisabled(false);
        setAlertState({ type: 'success', message: i18n.getMessage('pin.alert.sent') });
      } else if (currentConfirmationType !== TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION) {
        // Don't show "code sent" for push notifications
        setAlertState({ type: '', message: '' });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, currentConfirmationType]);

  return (
    <div
      className={`scheme-wrapper internal-action-confirmation ${
        isShowSwitchConfirmationMethodScreen ? 'switch-confirmation-method-screen' : ''
      }`}
    >
      <div className={'scheme-header'}>
        <p className={'title'}>{title}</p>
        <CloseIcon className={'scheme-close-icon'} onClick={onClose} />
      </div>

      {isShowSwitchConfirmationMethodScreen ? (
        <SwitchConfirmationMethodScreen
          onSwitchMethod={handleForceSwitchToAnotherConfirmationType}
          onBack={() => setIsShowSwitchConfirmationMethodScreen(false)}
          currentConfirmationType={currentConfirmationType}
          availableConfirmationTypes={getAvailableConfirmationTypes()}
        />
      ) : (
        <>
          <div className={'scheme-code-wrapper'}>
            <ConfirmCodeWrapper
              PhoneComponent={<strong>{getSecureUserPhone()}</strong>}
              isDisabled={isDisabled || currentConfirmationType === TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION}
              error={!error?.type && error}
              onComplete={onComplete}
              showCountDown={showCountDown}
              handleResendCode={handleResendCode}
              onTimerEnd={onTimerEnd}
              alertState={alertState}
              confirmationHandleType={CONFIRMATION_HANDLE_TYPES.ON_CHANGE}
              handleSwitchToAnotherMethod={() => setIsShowSwitchConfirmationMethodScreen(true)}
              isSwitchToAnotherConfirmationMethod={true}
            />
          </div>

          {currentConfirmationType !== TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION && (
            <div className={'scheme-footer'}>
              <Button
                className={'confirm-button'}
                type={'primary'}
                size={'large'}
                isLoading={isLoading}
                onClick={handleConfirm}
                isDisabled={isLoading || isConfirmDisabled}
              >
                {confirmButtonLabel}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

PopUpInternalActionConfirmationScheme.propTypes = {
  isLoading: PropTypes.bool,
  isShowSwitchConfirmationMethodScreen: PropTypes.bool,
  title: PropTypes.string,
  confirmButtonLabel: PropTypes.string,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  clearError: PropTypes.func,
  error: PropTypes.any,
  resendSecurityCode: PropTypes.func,
  setIsShowSwitchConfirmationMethodScreen: PropTypes.func,
  handleForceSwitchToAnotherConfirmationType: PropTypes.func,
  confirmationId: PropTypes.string,
  module: PropTypes.string
};

export default PopUpInternalActionConfirmationScheme;
